import * as Dialog from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/ui/Modal';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { AIBriefingWizard } from './AIBriefingWizard';

/**
 * AI Briefing Modal - "Mägic Ed"
 *
 * Simplified modal shell that delegates all functionality to AIBriefingWizard.
 * This component only handles the modal container and header.
 */

// ============================================================================
// Types
// ============================================================================

interface AIBriefingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInsertCaption: (caption: string) => void;
  onInsertImage?: (imageUrl: string, credit: string, downloadUrl: string) => void;
  userName?: string;
}

// ============================================================================
// Component
// ============================================================================

export function AIBriefingModal({
  isOpen,
  onClose,
  onInsertCaption,
  onInsertImage,
  userName,
}: AIBriefingModalProps) {
  const { t } = useTranslation('ai');

  return (
    <Modal
      open={isOpen}
      onOpenChange={onClose}
      size="xl"
      nested={true}
      raw={true}
    >
      {/* Hidden Dialog Title for Accessibility */}
      <VisuallyHidden>
        <Dialog.Title>{t('ai_briefing_title')}</Dialog.Title>
      </VisuallyHidden>

      {/* Fixed Header */}
      <div className="flex-shrink-0 border-b border-gray-200 px-6 py-4 bg-gradient-to-r from-primary-50 to-soft-mint rounded-t-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img
              src="/images/magic-ed.png"
              alt="Magic Ed"
              className="w-8 h-8 rounded-full object-cover"
            />
            <h2 className="text-2xl font-bold text-gray-900">
              {t('ai_briefing_title')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-white/50 transition-colors"
            aria-label={t('ai_briefing_close')}
          >
            <Icon name={ICONS.CLOSE} size="sm" />
          </button>
        </div>
      </div>

      {/* Wizard Content */}
      <div className="flex-1 overflow-hidden flex flex-col px-6 py-4">
        <AIBriefingWizard
          onClose={onClose}
          onInsertCaption={onInsertCaption}
          onInsertImage={onInsertImage}
          userName={userName}
        />
      </div>
    </Modal>
  );
}

export default AIBriefingModal;
